import React from 'react';
import { Button } from 'antd';
import { useMutation } from '@tanstack/react-query';
import useMyProfileService from '../../../../services/MyProfileService/myProfile.service';
import { useAuth } from '../../../../context/AuthContext';
import { AllowedRoles } from '../../../../routes/routeConstants/allowedRoles';
import { formatDisplayDate } from '../../../utils/formatDisplayDate';
import ConditionalRender from '../../ConditionalRender';
import { ProfileRenewalStatus, ProfileSubscription, ProfileSubscriptionStatus } from '../../../../models/profile.model';
import { getSubscriptionStatusLabel } from './utils';
import styles from './subscription.module.scss';

interface SubscriptionProps {
  subscription?: ProfileSubscription;
  renewalStatus?: ProfileRenewalStatus;
  subscriptionStatus?: ProfileSubscriptionStatus;
}

const Subscription: React.FC<SubscriptionProps> = ({ subscription, renewalStatus, subscriptionStatus }) => {
  const { canAccess } = useAuth();
  const { requestRenewal, requestSubscription } = useMyProfileService();
  const { mutate: renew, isPending: isRenewing } = useMutation(requestRenewal());
  const { mutate: subscribe, isPending: isSubscribing } = useMutation(requestSubscription());

  const canManageSubscription = canAccess({ allowedRoles: AllowedRoles.ONLY_ADMINS });
  const expiresAt = subscription?.expiresAt;
  const statusLabel = expiresAt ? getSubscriptionStatusLabel(expiresAt) : undefined;
  const addons = subscription?.addons ?? [];

  const handleRenew = () => renew();
  const handleSubscribe = () => subscribe();

  return (
    <section className={styles.section} aria-labelledby="profile-subscription">
      <h2 className={styles.heading} id="profile-subscription">
        Subscription
      </h2>

      <p className={styles.summary}>
        Your current MapAki subscription expires on <span className={styles.link}>{expiresAt ? formatDisplayDate(expiresAt) : '—'}</span>
        {statusLabel ? ` (${statusLabel})` : ''}.
      </p>

      <ConditionalRender visible={canManageSubscription}>
        <div className={styles.plans}>
          <div className={styles.plan}>
            <div className={styles.planInfo}>
              <span className={styles.planName}>{subscription?.type ? `${subscription.type} Subscription` : 'Subscription'}</span>
              <span className={styles.planDescription}>
                {expiresAt ? `Expires ${formatDisplayDate(expiresAt)}${statusLabel ? ` (${statusLabel})` : ''}.` : ''}
              </span>
            </div>
            <Button className={styles.button} disabled={!renewalStatus?.canRenew} loading={isRenewing} onClick={handleRenew}>
              Renew
            </Button>
          </div>

          <div className={styles.plan}>
            <div className={styles.planInfo}>
              <span className={styles.planName}>Additional Services</span>
              <ConditionalRender visible={addons.length > 0} hideFallback>
                <ul className={styles.addonList}>
                  {addons.map((addon) => (
                    <li className={styles.addonItem} key={addon.name}>
                      {addon.name}
                    </li>
                  ))}
                </ul>
              </ConditionalRender>
              <span className={styles.planDescription}>
                {subscriptionStatus?.canSubscribe ? 'Not currently subscribed to additional services.' : 'Subscription request already sent.'}
              </span>
            </div>
            <Button className={styles.button} disabled={!subscriptionStatus?.canSubscribe} loading={isSubscribing} onClick={handleSubscribe}>
              Subscribe
            </Button>
          </div>
        </div>
      </ConditionalRender>

      <p className={styles.contact}>
        To learn more about our subscription options, please <span className={styles.link}>contact us</span>.
      </p>
    </section>
  );
};

export default Subscription;
